/**
 * 화면용 글 만들기 — 금액·비율·시각(명세 0-6).
 *
 * ⚠ **금액은 내부 단위 정수 문자열이다**(계약 0-4). 1 USDE = 100. 거래소가 준
 * `"100500"` 은 1,005.00 USDE 다. 레거시 함수(소수 문자열용)를 그대로 쓰면 100배로
 * 보인다 — 그래서 바꿨다(명세 0-6 고침, 시험 6-3 M-01).
 *
 * ⚠ **float 로 바꾸지 않는다.** `Number("9223372036854775807")` 은 끝자리를 뭉갠다.
 * 문자열을 그대로 자르고 붙인다 — 큰 수도 정밀도를 잃지 않는다.
 *
 * ⚠ **USDE 에만 쓴다.** 사고 증빙의 금액은 OCR 이 영수증에서 읽은 **원화**라 여기서
 * 만지지 않는다 — 증거는 원문 그대로 보인다(명세 0-6 '원화 판독값').
 */

function group(int: string): string {
  return int.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
}

/** 정수 문자열을 100 으로 나눈 조각(부호·정수·소수 둘). 정수 모양이 아니면 null. */
function splitHundredths(value: string): { sign: string; int: string; frac: string } | null {
  const m = /^(-?)(\d+)$/.exec(value)
  if (!m) return null
  const digits = (m[2] ?? '0').replace(/^0+(?=\d)/, '').padStart(3, '0')
  const int = digits.slice(0, -2).replace(/^0+(?=\d)/, '')
  const frac = digits.slice(-2)
  const zero = /^0+$/.test(int) && frac === '00'
  return { sign: zero ? '' : (m[1] ?? ''), int, frac }
}

/**
 * USDE 내부 단위 → 「1,005.00」.
 *
 * `"0"`→0.00, `"1"`→0.01, `"-50"`→-0.50, `"100500"`→1,005.00. null·빈 값은 null
 * ([Maybe] 가 —로 그린다). 내부 단위 모양(부호 있는 정수)이 아니면 원문 그대로.
 */
export function formatUsde(value: string | number | null | undefined): string | null {
  if (value === null || value === undefined) return null
  const s = String(value).trim()
  if (s === '') return null
  const p = splitHundredths(s)
  if (!p) return s
  return `${p.sign}${group(p.int)}.${p.frac}`
}

/** 금액 + 「 USDE」. 값이 없으면 null. */
export function usde(value: string | number | null | undefined): string | null {
  const f = formatUsde(value)
  return f === null ? null : `${f} USDE`
}

/**
 * 1/100 퍼센트(bp) → 「12.34%」. `drainedBp`·BP 설정(`3000` → 30.00%)에 쓴다.
 * 문자열 자르기로 만든다(명세 0-6 '비율').
 */
export function formatBp(value: string | number | null | undefined): string | null {
  if (value === null || value === undefined) return null
  const s = String(value).trim()
  if (s === '') return null
  const p = splitHundredths(s)
  if (!p) return s
  return `${p.sign}${p.int}.${p.frac}%`
}

/*
 * ── 시각(명세 0-6 '시각') ─────────────────────────────────────────────────
 *
 * 사장님 2026-10-01 「시간 표기는 yyyy-mm-dd HH:mm:ss 이해됬니」 — 관리자 웹의 모든 시각을 이 모양으로
 * (예 2026-10-01 14:20:05). 시간대는 관리자 컴퓨터 시간(질문 41 「2」). 날짜만 받는 칸은 앞 반
 * 「yyyy-mm-dd」. 서버와는 늘 UTC 밀리초로 주고받는다(계약 0-5) — 바꾸는 것은 화면 글뿐이다.
 *
 * ⚠ `toLocaleString` 을 쓰지 않는다. 브라우저 언어에 따라 「2026. 10. 1. 오후 2:20:05」처럼 모양이
 * 바뀐다. 자리마다 0 을 채워 직접 만든다.
 */

/** 시각을 받는 칸의 모양 안내(placeholder). */
export const DATE_TIME_FORMAT = 'yyyy-mm-dd HH:mm:ss'
/** 날짜만 받는 칸의 모양 안내(placeholder). */
export const DATE_FORMAT = 'yyyy-mm-dd'

function pad(n: number, width = 2): string {
  return String(n).padStart(width, '0')
}

function ymd(d: Date): string {
  return `${pad(d.getFullYear(), 4)}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
}

/** 컴퓨터 시간대의 그 시각. 숫자가 있는 날짜·시각이 아니면(2026-02-30, 24:00:00) null. */
function localDate(y: number, mo: number, d: number, h = 0, mi = 0, s = 0): Date | null {
  const t = new Date(2000, 0, 1)
  // ⚠ setFullYear 로 넣는다 — new Date(99, …) 는 1999 년이 된다.
  t.setFullYear(y, mo - 1, d)
  t.setHours(h, mi, s, 0)
  const same = t.getFullYear() === y && t.getMonth() === mo - 1 && t.getDate() === d
    && t.getHours() === h && t.getMinutes() === mi && t.getSeconds() === s
  return same ? t : null
}

/** 밀리초 → 「2026-10-01 14:20:05」(관리자 컴퓨터 시간대). 없으면 null. */
export function formatTime(ms: number | null | undefined): string | null {
  if (ms === null || ms === undefined || !Number.isFinite(ms)) return null
  const d = new Date(ms)
  return `${ymd(d)} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`
}

/** 오늘 날짜 「yyyy-mm-dd」(관리자 컴퓨터 시간대). */
export function localToday(now: Date = new Date()): string {
  return ymd(now)
}

/**
 * 「yyyy-mm-dd」 하루 → [그날 00:00, 다음 날 00:00) 밀리초(관리자 컴퓨터 시간대, to 미포함).
 * 모양이 틀리거나 없는 날(2026-02-30)이면 null.
 */
export function localDayRange(day: string): { from: number; to: number } | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(day)
  if (!m) return null
  const start = localDate(Number(m[1]), Number(m[2]), Number(m[3]))
  if (!start) return null
  const next = new Date(start.getTime())
  next.setDate(next.getDate() + 1)
  return { from: start.getTime(), to: next.getTime() }
}

/**
 * 사람이 친 「yyyy-mm-dd HH:mm:ss」 → 밀리초(관리자 컴퓨터 시간대). 모양이 틀리거나 없는 시각
 * (2026-02-30, 24:00:00)이면 null — 보내지 않는다.
 */
export function parseLocalDateTime(text: string): number | null {
  const m = /^(\d{4})-(\d{2})-(\d{2}) (\d{2}):(\d{2}):(\d{2})$/.exec(text.trim())
  if (!m) return null
  const t = localDate(Number(m[1]), Number(m[2]), Number(m[3]), Number(m[4]), Number(m[5]), Number(m[6]))
  return t === null ? null : t.getTime()
}

/**
 * 사람이 친 USDE 금액 → 서버로 보낼 **내부 단위 정수 문자열**(시험 6-3 M-08).
 *
 * `"10"`→`"1000"`, `"10.5"`→`"1050"`, `"0.01"`→`"1"`, `"2,000"`→`"200000"`.
 * 소수 셋째 자리·음수·빈 값·지수 표기·글자는 null(보내지 않는다).
 *
 * ⚠ **float 을 쓰지 않는다.** `Math.round(10.1 * 100)` 같은 것은 어느 날 한 unit 을
 * 틀린다 — 돈 기준 설정(주문 최소·최대)에서 한 unit 은 결함이다.
 */
export function parseUsdeInput(input: string): string | null {
  const s = input.trim()
  if (s === '') return null
  const plain = /^\d+(\.\d{1,2})?$/.test(s)
  const grouped = /^\d{1,3}(,\d{3})+(\.\d{1,2})?$/.test(s)
  if (!plain && !grouped) return null
  const [int = '0', frac = ''] = s.replace(/,/g, '').split('.')
  return (int + frac.padEnd(2, '0')).replace(/^0+(?=\d)/, '')
}

/**
 * 밀리초 길이 → 「2일 3시간 4분」(분 아래는 버림 — 보이기만). 0 이면 「없음」, 없으면 null.
 * 월 사용료의 멈춘 시간(`monthlyFee.pausedMs` — 확인 19)에 쓴다.
 */
export function formatDuration(ms: number | null | undefined): string | null {
  if (ms === null || ms === undefined || !Number.isFinite(ms) || ms < 0) return null
  const totalMin = Math.floor(ms / 60_000)
  if (totalMin === 0) return ms === 0 ? 'None' : 'Under 1 min'
  const d = Math.floor(totalMin / 1440)
  const h = Math.floor((totalMin % 1440) / 60)
  const m = totalMin % 60
  return [d > 0 ? `${d}d` : '', h > 0 ? `${h}h` : '', m > 0 ? `${m}m` : ''].filter(Boolean).join(' ')
}

/**
 * 밀리초 또는 `YYYY-MM-DD` — 숫자면 시각으로, 글이면 그대로. 없으면 null.
 * 글은 서버가 날짜(LocalDate)로 준 것이라 이미 「yyyy-mm-dd」다.
 */
export function formatWhen(v: number | string | null | undefined): string | null {
  if (v === null || v === undefined || v === '') return null
  return typeof v === 'number' ? formatTime(v) : v
}

